import { Injectable } from "@angular/core";
import { ApiService } from "../global.service";
import { Observable, map } from "rxjs";
import { HttpBackend, HttpClient } from "@angular/common/http";

import { MenuProduct, MenuProductGroup } from "../../models/menu-product.model";
import { ConfigService } from "../config.service";
import { LocalStorageService } from "../storage.service";
import { BusinessContextService } from "../business-context.service";

@Injectable({
    providedIn: 'root'
})
export class MenuProductService extends ApiService {
    constructor(
        public override http: HttpClient, 
        public override config: ConfigService,
        public override localStorageService: LocalStorageService,
        handler: HttpBackend,
        private readonly bizCtx: BusinessContextService,
    ) {
        super(http, config, localStorageService, handler);
    }

    private getBusiness(): string { return this.bizCtx.getSelected() || this.CUSTOMER; }

    getCatalog(tableUuid?: string | null, orderUuid?: string | null): Observable<MenuProduct[]> {
        const business = this.getBusiness();
        const params: Record<string, string> = {};
        if (tableUuid) params['tableUuid'] = tableUuid.toString();
        if (orderUuid) params['orderUuid'] = orderUuid.toString();
        return this.get<MenuProduct[]>(`${this.URL}/menu-product/catalog/${business}`, Object.keys(params).length ? params : undefined);
    }

    getMenuProducts(): Observable<MenuProduct[]> {
        const business = this.getBusiness();
        return this.get<MenuProduct[]>(`${this.URL}/menu-product/${business}`);
    }

    getMenuProductsGroupedByCategory(): Observable<MenuProductGroup[]> {
        const business = this.getBusiness();
        return this.get<any>(`${this.URL}/menu-product/${business}`).pipe(
            map((payload: any) => {
                if (!Array.isArray(payload)) return [] as MenuProductGroup[];

                // Case A: Already grouped [{ category: {name, uuid}, items: [...] }]
                const looksGrouped = payload.length > 0 && payload[0]?.category && Array.isArray(payload[0]?.items);
                if (looksGrouped) {
                    const mapped: MenuProductGroup[] = payload.map((grp: any) => {
                        const cat = grp.category || {};
                        const id = cat.uuid || cat.hash || cat.id || cat.name || 'unknown';
                        const name = cat.name || 'Sin categoría';
                        // trust items as MenuProduct[] and pass through (our model tolerates fields)
                        const items: MenuProduct[] = Array.isArray(grp.items) ? grp.items : [];
                        return { categoryId: String(id), categoryName: String(name), items } as MenuProductGroup;
                    });
                    return mapped;
                }

                // Case B: Flat list -> group on client as before
                const products = payload as MenuProduct[];
                const groups = new Map<string, MenuProductGroup>();
                products.forEach((product: MenuProduct) => {
                    const key = String(
                        product.categoryHash ||
                        product.categoryUuid ||
                        product.categoryId ||
                        product.category?.uuid ||
                        product.category?.hash ||
                        product.category?.id ||
                        'unknown'
                    );

                    const name = String(
                        product.categoryName ||
                        product.category?.name ||
                        'Sin categoría'
                    );

                    if (!groups.has(key)) {
                        groups.set(key, { categoryId: String(key), categoryName: String(name), items: [] });
                    }
                    groups.get(key)!.items.push(product);
                });
                return Array.from(groups.values());
            })
        );
    }

    createMenuProduct(menuProductInfo: Omit<MenuProduct, 'id'>): Observable<MenuProduct> {
        const business = this.getBusiness();
        return this.create<MenuProduct>(`${this.URL}/menu-product/${business}`, menuProductInfo);
    }

    updateProduct(menuProduct: MenuProduct): Observable<MenuProduct> {
        const m: any = menuProduct as any;
        const uuid = m.uuid as string;
        if (!uuid) {
            console.error('[MenuProductService] Missing menuProduct.uuid for update. Payload:', m);
            throw new Error('menuProduct.uuid is required for update');
        }
        const business = this.getBusiness();
        const url = `${this.URL}/menu-product/${business}/${uuid}`;
        return this.update<MenuProduct>(url, menuProduct);
    }

    deleteProduct(productId: string): Observable<void> {
        const business = this.getBusiness();
        return this.http.delete<void>(`${this.URL}/menu-product/${business}/${productId}`);
    }

    validateIfExist(field: string, value: string): Observable<{ exists: boolean }> {
        const business = this.getBusiness();
        return this.get<{ exists: boolean }>(`${this.URL}/menu-product/${business}/validate?field=${field}&value=${value}`);
    }

    getPriceHistory(productUuid: string, page = 1, perPage = 20): Observable<any[]> {
        const business = this.getBusiness();
        return this.get<any[]>(`${this.URL}/menu-product/${business}/${productUuid}/price-history`, { page: page.toString(), perPage: perPage.toString() });
    }

    addPriceHistory(productUuid: string, payload: { sellPrice: number; costPrice: number; date: string }): Observable<any> {
        const business = this.getBusiness();
        return this.create<any>(`${this.URL}/menu-product/${business}/${productUuid}/price-history`, payload);
    }
}
